import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CreditPurchaseModal from '../../components/credits/CreditPurchaseModal';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useHasPermission } from '../../hooks/usePermission';
import { getBillingHistory, fetchBalance } from '../../redux/slices/creditSlice';
import ForbiddenPage from '../ForbiddenPage';

/**
 * BillingPage - Ödeme & Fatura sayfası
 * 
 * Features:
 * - Fatura ve ödeme geçmişi (ileride eklenecek)
 * - Abonelik yönetimi (ileride eklenecek)
 * - Kredi/ödeme işlemleri (ileride eklenecek)
 * 
 * Phase: Billing & Payments
 */
const BillingPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const hasPermission = useHasPermission('billing.view');
  const hasPurchasePermission = useHasPermission('credits.purchase');
  
  const { billingHistory, billingLoading, billingError, balance } = useSelector(state => state.credits);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (hasPermission) {
      dispatch(getBillingHistory());
      dispatch(fetchBalance()); // Mevcut bakiyeyi ayrı fetch et
    }
  }, [dispatch, hasPermission]);

  const handleBack = () => {
    history.goBack();
  };

  // Permission-based guard check
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Geri Dön
      </Button>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Ödeme & Fatura
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fatura ve ödeme geçmişinizi buradan görüntüleyebilirsiniz
          </Typography>
        </Box>
        {hasPurchasePermission && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Kredi Yükle
          </Button>
        )}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Toplam Harcama
              </Typography>
              <Typography variant="h4" color="primary">
                {billingHistory?.summary?.totalSpent?.toFixed(2) || '0.00'} TL
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Toplam Satın Alınan Kredi
              </Typography>
              <Typography variant="h4" color="info.main">
                {billingHistory?.summary?.totalPurchasedCredits?.toFixed(2) || '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Mevcut Bakiye
              </Typography>
              <Typography variant="h4" color="success.main">
                {(billingHistory?.summary?.currentBalance ?? balance ?? 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Eldeki toplam kredi
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Loading State */}
      {billingLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {billingError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {billingError}
        </Alert>
      )}

      {/* Transaction Table */}
      {!billingLoading && !billingError && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Tarih</strong></TableCell>
                <TableCell><strong>Paket Adı</strong></TableCell>
                <TableCell align="right"><strong>Tutar (TL)</strong></TableCell>
                <TableCell align="right"><strong>Kredi Miktarı</strong></TableCell>
                <TableCell align="center"><strong>Durum</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billingHistory?.transactions?.length > 0 ? (
                billingHistory.transactions.map((transaction) => (
                  <TableRow key={transaction._id}>
                    <TableCell>
                      {transaction.createdAt
                        ? format(new Date(transaction.createdAt), 'dd MMM yyyy, HH:mm', { locale: tr })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {transaction.type === 'manual_add' 
                        ? transaction.description || 'Manuel Yükleme'
                        : transaction.description || transaction.packageId?.name || '-'}
                    </TableCell>
                    <TableCell align="right">
                      {transaction.paymentAmount ? `${transaction.paymentAmount.toFixed(2)} TL` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {transaction.amount?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label="Başarılı"
                        color="success"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Henüz ödeme geçmişi bulunmamaktadır.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Purchase Modal */}
      <CreditPurchaseModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          dispatch(fetchBalance());
          dispatch(getBillingHistory());
          setModalOpen(false);
        }}
      />
    </Container>
  );
};

export default BillingPage;

